import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Droplets, Sparkles, Moon, Hand } from 'lucide-react';
import { playWateringSound, WATER_PER_POUR } from '../utils/wateringUtils';

interface ManualGardenCanProps {
  gardenRef: React.RefObject<HTMLDivElement>;
  canWaterLevel: number;
  isWatering: boolean;
  targetFlowerIndex: number | null;
  countdownText?: string;
  isAdmin?: boolean;
  onPourWater: (flowerIndex: number) => Promise<void> | void;
  onRefillCan?: () => Promise<void> | void;
}

export const ManualGardenCan: React.FC<ManualGardenCanProps> = React.memo(({
  gardenRef,
  canWaterLevel,
  isWatering,
  targetFlowerIndex,
  countdownText,
  isAdmin = false,
  onPourWater,
  onRefillCan,
}) => {
  const [canPos, setCanPos] = useState<{ x: number; y: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isTilting, setIsTilting] = useState(false);
  const [showEmptyNotice, setShowEmptyNotice] = useState(false);
  const [hoveredFlowerIndex, setHoveredFlowerIndex] = useState<number | null>(null);
  const [activeFlowerTarget, setActiveFlowerTarget] = useState<number | null>(null);
  const [lastWaterDeduction, setLastWaterDeduction] = useState<number | null>(null);
  const [dragAngle, setDragAngle] = useState(0);
  const [hasInteractedOnce, setHasInteractedOnce] = useState(() => {
    try {
      return localStorage.getItem('garden_can_drag_guided') === 'true';
    } catch {
      return false;
    }
  });

  const isEmpty = canWaterLevel <= 0;
  const canRef = useRef<HTMLDivElement>(null);

  // Measure initial fixed resting station on the lawn dock
  const getInitialRestPos = useCallback(() => {
    if (!gardenRef.current) return { x: 260, y: 380 };
    const gardenRect = gardenRef.current.getBoundingClientRect();
    const isMobile = gardenRect.width < 640;

    const dockEl = document.getElementById('garden-watering-can-dock');
    if (dockEl) {
      const dRect = dockEl.getBoundingClientRect();
      const rawX = dRect.left + dRect.width / 2 - gardenRect.left;
      // Position center of can so its base rests directly on the lawn shadow
      const rawY = dRect.bottom - 44 - gardenRect.top;
      return {
        // Enforce safe margin from left and right screen boundaries so can and labels never clip
        x: Math.max(isMobile ? 44 : 65, Math.min(gardenRect.width - (isMobile ? 44 : 65), Math.round(rawX))),
        // Enforce safe clearance above Counter HUD and bottom bars
        y: Math.max(80, Math.min(gardenRect.height - (isMobile ? 190 : 175), Math.round(rawY))),
      };
    }

    // Reliable responsive fallback on right side of meadow lawn safely inside bounds
    const safeX = Math.max(isMobile ? 44 : 65, Math.min(gardenRect.width - (isMobile ? 44 : 65), gardenRect.width * 0.82));
    const safeY = Math.max(80, Math.min(gardenRect.height - (isMobile ? 190 : 175), gardenRect.height * 0.62));
    return {
      x: Math.round(safeX),
      y: Math.round(safeY),
    };
  }, [gardenRef]);

  // Target coordinates above the specific flower crown
  const getFlowerTargetCoords = useCallback((idx: number) => {
    if (!gardenRef.current) {
      const xP = [16, 36, 56, 76][idx] ?? 50;
      return {
        x: Math.round((window.innerWidth * xP) / 100 + 44),
        y: Math.round(window.innerHeight * 0.48),
      };
    }

    const gardenRect = gardenRef.current.getBoundingClientRect();
    const flowerEl = document.getElementById(`flower-anchor-${idx}`);

    if (flowerEl) {
      const headEl =
        flowerEl.querySelector('.head') ||
        flowerEl.querySelector('.head-wind') ||
        flowerEl.querySelector('.mvd-flower') ||
        flowerEl;
      const hRect = headEl.getBoundingClientRect();

      const flowerCenterX = hRect.left + hRect.width / 2 - gardenRect.left;
      const flowerCrownTopY = hRect.top - gardenRect.top;

      // Position can so its tilted nozzle hovers ~46px directly above the petals
      // Offset 46px to the right so nozzle aligns directly over flower center
      return {
        x: Math.round(flowerCenterX + 46),
        y: Math.max(40, Math.round(flowerCrownTopY - 58)),
        flowerCenterX: Math.round(flowerCenterX),
        flowerCrownTopY: Math.round(flowerCrownTopY),
      };
    }

    const colW = gardenRect.width / 4;
    return {
      x: Math.round(colW * idx + colW / 2 + 36),
      y: Math.round(gardenRect.height * 0.54),
      flowerCenterX: Math.round(colW * idx + colW / 2),
      flowerCrownTopY: Math.round(gardenRect.height * 0.54 + 50),
    };
  }, [gardenRef]);

  // Initialize and maintain position on mount & resize
  useEffect(() => {
    const updatePos = () => {
      setCanPos((prev) => {
        if (!prev) return getInitialRestPos();
        // If not dragging or tilting, ensure it stays safely inside the resting dock
        if (!isDragging && !isTilting) {
          return getInitialRestPos();
        }
        return prev;
      });
    };

    updatePos();
    const timer = setTimeout(updatePos, 200);

    window.addEventListener('resize', updatePos);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updatePos);
    };
  }, [getInitialRestPos, isDragging, isTilting]);

  // Master individual pouring action
  const triggerWaterFlower = useCallback(async (flowerIdx: number) => {
    if (isWatering || isTilting) return;

    if (isEmpty) {
      setShowEmptyNotice(true);
      setTimeout(() => setShowEmptyNotice(false), 3600);
      return;
    }

    if (!hasInteractedOnce) {
      setHasInteractedOnce(true);
      try {
        localStorage.setItem('garden_can_drag_guided', 'true');
      } catch {}
    }

    const targetPos = getFlowerTargetCoords(flowerIdx);
    setActiveFlowerTarget(flowerIdx);
    setCanPos({ x: targetPos.x, y: targetPos.y });
    setIsTilting(true);
    playWateringSound();
    setLastWaterDeduction(WATER_PER_POUR);

    setTimeout(() => setLastWaterDeduction(null), 1800);

    try {
      await onPourWater(flowerIdx);
    } finally {
      setTimeout(() => {
        setIsTilting(false);
        setActiveFlowerTarget(null);
        // Smoothly glide back to resting spot on meadow
        setCanPos(getInitialRestPos());
      }, 2000);
    }
  }, [isWatering, isTilting, isEmpty, hasInteractedOnce, getFlowerTargetCoords, onPourWater, getInitialRestPos]);

  // Handle external watering triggers (e.g. from top bar or flower click)
  useEffect(() => {
    if (!isWatering) {
      if (!isDragging) {
        setIsTilting(false);
        setActiveFlowerTarget(null);
      }
      return;
    }

    if (targetFlowerIndex !== null) {
      // Single flower targeted: move above that flower and tilt
      const coords = getFlowerTargetCoords(targetFlowerIndex);
      setActiveFlowerTarget(targetFlowerIndex);
      setCanPos({ x: coords.x, y: coords.y });
      setIsTilting(true);
      setLastWaterDeduction(WATER_PER_POUR);

      const t = setTimeout(() => {
        setIsTilting(false);
        setLastWaterDeduction(null);
        setActiveFlowerTarget(null);
        setCanPos(getInitialRestPos());
      }, 2100);
      return () => clearTimeout(t);
    } else {
      // All flowers watering sequence (Tour across 0 -> 1 -> 2 -> 3)
      setIsTilting(true);
      setLastWaterDeduction(100);

      const sequence = [0, 1, 2, 3];
      sequence.forEach((idx, step) => {
        setTimeout(() => {
          const coords = getFlowerTargetCoords(idx);
          setActiveFlowerTarget(idx);
          setCanPos({ x: coords.x, y: coords.y });
        }, step * 720);
      });

      const finishTimeout = setTimeout(() => {
        setIsTilting(false);
        setLastWaterDeduction(null);
        setActiveFlowerTarget(null);
        setCanPos(getInitialRestPos());
      }, sequence.length * 720 + 300);

      return () => clearTimeout(finishTimeout);
    }
  }, [isWatering, targetFlowerIndex, getFlowerTargetCoords, getInitialRestPos, isDragging]);

  // Proximity detection while dragging across the entire screen
  const handleDrag = useCallback((_event: any, info: any) => {
    if (!gardenRef.current) return;
    const gardenRect = gardenRef.current.getBoundingClientRect();
    const curX = info.point.x - gardenRect.left;
    const curY = info.point.y - gardenRect.top;

    // Velocity-based natural can tilt during drag
    const vx = info.velocity?.x || 0;
    const dynamicTilt = Math.max(-16, Math.min(16, vx * 0.04));
    setDragAngle(dynamicTilt);

    // Find nearest flower within touch range
    let nearest: number | null = null;
    let minDist = 100;

    [0, 1, 2, 3].forEach((idx) => {
      const coords = getFlowerTargetCoords(idx);
      const dist = Math.hypot(curX - coords.x, curY - coords.y);
      if (dist < minDist) {
        minDist = dist;
        nearest = idx;
      }
    });

    setHoveredFlowerIndex(nearest);
  }, [gardenRef, getFlowerTargetCoords]);

  // Drag End: water flower if near, or glide back to fixed resting spot
  const handleDragEnd = useCallback((_event: any, _info: any) => {
    setIsDragging(false);
    setDragAngle(0);

    const chosen = hoveredFlowerIndex;
    setHoveredFlowerIndex(null);

    if (chosen !== null) {
      // Near a flower: water it!
      triggerWaterFlower(chosen);
    } else {
      // Released in open space: gracefully glide back home to resting meadow dock!
      setCanPos(getInitialRestPos());
    }
  }, [hoveredFlowerIndex, triggerWaterFlower, getInitialRestPos]);

  // Current active coordinates
  const pos = canPos || getInitialRestPos();

  // Calculate precise world coordinates of the sprinkler rose head nozzle
  // when the can is positioned at (pos.x, pos.y) and tilted -38 degrees
  const nozzleWorldPos = useMemo(() => {
    // Can box is 80x80px centered at (pos.x, pos.y)
    // At rotate(-38deg) with transformOrigin: '55% 55%',
    // nozzle tip offset from can center is approximately dx = -32px, dy = +16px
    return {
      x: pos.x - 32,
      y: pos.y + 16,
    };
  }, [pos]);

  // Target flower details for water cascade
  const targetFlowerDetails = useMemo(() => {
    if (activeFlowerTarget === null) return null;
    return getFlowerTargetCoords(activeFlowerTarget);
  }, [activeFlowerTarget, getFlowerTargetCoords]);

  return (
    <div
      className="absolute inset-0 pointer-events-none z-40 overflow-hidden select-none"
      id="manual-watering-can-layer"
    >
      {/* 1. VISUAL TARGET GUIDE FOR FLOWERS (Highlights when dragging near a flower) */}
      {[0, 1, 2, 3].map((idx) => {
        const isHovered = hoveredFlowerIndex === idx;
        const coords = getFlowerTargetCoords(idx);

        if (!isHovered) return null;

        return (
          <div
            key={idx}
            style={{
              position: 'absolute',
              left: `${coords.flowerCenterX}px`,
              top: `${coords.flowerCrownTopY}px`,
              transform: 'translate(-50%, -100%)',
            }}
            className="pointer-events-none z-30 flex flex-col items-center"
          >
            <AnimatePresence>
              <motion.div
                initial={{ opacity: 0, scale: 0.8, y: 6 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.8, y: -4 }}
                className="mb-1 px-3 py-1 bg-sky-600/95 text-white rounded-full text-[11px] font-bold shadow-xl border border-sky-300/80 backdrop-blur-md flex items-center gap-1.5 whitespace-nowrap"
              >
                <Droplets className="w-3.5 h-3.5 fill-current text-sky-200 animate-bounce" />
                <span>Soltar para regar Flor {idx + 1}</span>
              </motion.div>
            </AnimatePresence>
            <div className="w-14 h-6 rounded-full bg-sky-400/25 border-2 border-dashed border-sky-300 scale-110 animate-pulse shadow-[0_0_16px_rgba(56,189,248,0.6)]" />
          </div>
        );
      })}

      {/* 2. ELEGANT WATERING FEEDBACK PILL (Right above the flower being watered) */}
      <AnimatePresence>
        {isTilting && activeFlowerTarget !== null && targetFlowerDetails && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.25 }}
            style={{
              position: 'absolute',
              left: `${targetFlowerDetails.flowerCenterX}px`,
              top: `${targetFlowerDetails.flowerCrownTopY - 20}px`,
              transform: 'translate(-50%, -100%)',
            }}
            className="px-3 py-1 rounded-full bg-slate-900/90 text-sky-200 border border-sky-400/50 backdrop-blur-md shadow-xl flex items-center gap-1.5 text-xs font-semibold z-50 whitespace-nowrap"
          >
            <Sparkles className="w-3.5 h-3.5 text-amber-300 animate-spin" style={{ animationDuration: '3s' }} />
            <span>¡Regando Flor {activeFlowerTarget + 1}! 💧</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 3. FLUID WATER CASCADE EFFECT (Rendered in true screen/gravity coordinates, un-rotated) */}
      {isTilting && !isEmpty && targetFlowerDetails && (
        <div
          style={{
            position: 'absolute',
            left: `${nozzleWorldPos.x}px`,
            top: `${nozzleWorldPos.y}px`,
          }}
          className="pointer-events-none z-45 overflow-visible"
        >
          {(() => {
            // Delta vector from rose nozzle tip to flower crown petals
            const targetX = targetFlowerDetails.flowerCenterX - nozzleWorldPos.x;
            const targetY = Math.max(30, targetFlowerDetails.flowerCrownTopY - nozzleWorldPos.y + 4);
            const leftBound = Math.min(0, targetX) - 25;
            const rightBound = Math.max(0, targetX) + 25;
            const width = Math.max(70, rightBound - leftBound);
            const height = Math.max(70, targetY + 30);
            const originX = -leftBound; // Exact position of nozzle in this coordinate frame
            const originY = 2; // Near top of nozzle tip
            const impactX = originX + targetX;
            const impactY = originY + targetY;

            return (
              <div
                style={{
                  position: 'absolute',
                  left: `${leftBound}px`,
                  top: '0px',
                  width: `${width}px`,
                  height: `${height}px`,
                }}
                className="overflow-visible"
              >
                {/* SVG Arching Flow Streams */}
                <svg
                  className="w-full h-full overflow-visible"
                  viewBox={`0 0 ${width} ${height}`}
                >
                  <defs>
                    <linearGradient id="streamGradTrue" x1="50%" y1="0%" x2="50%" y2="100%">
                      <stop offset="0%" stopColor="#ffffff" stopOpacity="0.95" />
                      <stop offset="25%" stopColor="#bae6fd" stopOpacity="0.9" />
                      <stop offset="65%" stopColor="#38bdf8" stopOpacity="0.75" />
                      <stop offset="100%" stopColor="#0284c7" stopOpacity="0.4" />
                    </linearGradient>
                  </defs>

                  {/* 5 natural parabolic water stream rays exiting nozzle holes */}
                  {[-6, -3, 0, 3, 6].map((spreadOffset, sIdx) => {
                    const endX = impactX + spreadOffset;
                    const endY = impactY;
                    const midX = originX + (targetX + spreadOffset) * 0.45;
                    const midY = originY + targetY * 0.48;

                    return (
                      <path
                        key={`water-ray-${sIdx}`}
                        d={`M ${originX} ${originY} Q ${midX} ${midY}, ${endX} ${endY}`}
                        fill="none"
                        stroke="url(#streamGradTrue)"
                        strokeWidth={sIdx === 2 ? 3.0 : 2.2}
                        strokeLinecap="round"
                        className="animate-pulse"
                        style={{ animationDuration: `${0.4 + sIdx * 0.08}s` }}
                      />
                    );
                  })}
                </svg>

                {/* 22 Animated Falling Droplets Accelerating Down with Gravity */}
                {Array.from({ length: 22 }).map((_, i) => {
                  const spread = ((i % 5) - 2) * 5;
                  const destX = impactX + spread;
                  const destY = impactY;

                  return (
                    <motion.span
                      key={`gravity-drop-${i}`}
                      initial={{
                        opacity: 0.95,
                        x: originX + (i % 3) - 1,
                        y: originY,
                        scale: 0.7,
                      }}
                      animate={{
                        opacity: [0.95, 0.9, 0],
                        x: [originX + (i % 3) - 1, destX],
                        y: [originY, destY],
                        scale: [0.8, 1.25, 0.35],
                      }}
                      transition={{
                        duration: 0.42 + (i % 5) * 0.06,
                        repeat: Infinity,
                        repeatDelay: (i % 4) * 0.05,
                        ease: 'easeIn', // Real gravitational acceleration!
                      }}
                      className="absolute w-2 h-2.5 rounded-full pointer-events-none"
                      style={{
                        background: 'radial-gradient(ellipse at 35% 25%, #ffffff 0%, #7dd3fc 45%, #0284c7 100%)',
                        boxShadow: '0 0 6px rgba(186, 230, 253, 0.95)',
                      }}
                    />
                  );
                })}

                {/* Petal Impact Splash Particles */}
                <div
                  style={{
                    position: 'absolute',
                    left: `${impactX}px`,
                    top: `${impactY}px`,
                    transform: 'translate(-50%, -50%)',
                  }}
                  className="w-10 h-6 flex items-center justify-center pointer-events-none"
                >
                  {Array.from({ length: 8 }).map((_, sp) => (
                    <motion.span
                      key={`impact-splash-${sp}`}
                      initial={{ opacity: 0, scale: 0.3, y: 0, x: 0 }}
                      animate={{
                        opacity: [0, 0.95, 0],
                        scale: [0.3, 1.2, 0.2],
                        y: [-2, -12 - (sp % 3) * 4],
                        x: [(sp - 3.5) * 6],
                      }}
                      transition={{
                        duration: 0.35,
                        repeat: Infinity,
                        repeatDelay: (sp % 3) * 0.07,
                      }}
                      className="absolute w-1.5 h-1.5 rounded-full bg-white shadow-[0_0_8px_#7dd3fc]"
                    />
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* 4. FLOATING EMPTY NOTICE TOAST */}
      <AnimatePresence>
        {showEmptyNotice && (
          <motion.div
            initial={{ opacity: 0, y: 15, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            className="absolute top-16 left-1/2 -translate-x-1/2 px-4 py-2.5 bg-slate-950/95 text-amber-200 backdrop-blur-md rounded-2xl border border-amber-400/40 shadow-2xl flex flex-wrap items-center justify-center gap-2 text-xs font-medium z-50 pointer-events-auto max-w-[92%] sm:max-w-md text-center"
          >
            <Moon className="w-4 h-4 text-amber-300 shrink-0" />
            <span>
              ¡Regadera vacía (0%)! Se recargará en 24 horas
              {countdownText ? ` (${countdownText})` : ''} 💧
            </span>
            {onRefillCan && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onRefillCan();
                  setShowEmptyNotice(false);
                }}
                className="ml-1 px-2.5 py-1 rounded-full bg-sky-500 hover:bg-sky-400 active:scale-95 text-white font-bold text-[11px] shadow-md transition-all cursor-pointer whitespace-nowrap"
              >
                Recargar ahora ⚡
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* 5. FLOATING WATER DEDUCTION BADGE */}
      <AnimatePresence>
        {lastWaterDeduction && (
          <motion.div
            initial={{ opacity: 0, y: 0, scale: 0.8 }}
            animate={{ opacity: 1, y: -45, scale: 1.1 }}
            exit={{ opacity: 0, y: -65, scale: 0.9 }}
            transition={{ duration: 1.2, ease: 'easeOut' }}
            style={{
              position: 'absolute',
              left: `${pos.x}px`,
              top: `${pos.y - 20}px`,
              transform: 'translateX(-50%)',
            }}
            className="px-3 py-1 bg-sky-600/95 text-white rounded-full shadow-2xl border border-sky-300/80 flex items-center gap-1.5 text-xs font-bold pointer-events-none z-50 whitespace-nowrap"
          >
            <Droplets className="w-3.5 h-3.5 fill-current" />
            <span>-{lastWaterDeduction}% Agua • ¡Flor hidratada! 🌱</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 6. FIRST-TIME DRAG GUIDANCE BADGE */}
      <AnimatePresence>
        {!hasInteractedOnce && !isDragging && !isTilting && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: [0, -4, 0] }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ y: { repeat: Infinity, duration: 2.2, ease: 'easeInOut' } }}
            style={{
              position: 'absolute',
              left: `${pos.x}px`,
              top: `${pos.y - 34}px`,
              transform: 'translateX(-50%)',
            }}
            className="px-2.5 py-0.5 bg-slate-900/90 text-sky-200 text-[10px] sm:text-xs font-medium rounded-full border border-sky-400/40 backdrop-blur-md shadow-lg flex items-center gap-1.5 pointer-events-none z-40 whitespace-nowrap"
          >
            <Hand className="w-3 h-3 text-sky-300 animate-pulse" />
            <span>Toca o arrastra para regar</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 7. THE MASTER PHYSICAL WATERING CAN */}
      <motion.div
        ref={canRef}
        drag={!isEmpty}
        dragConstraints={gardenRef}
        dragElastic={0.06}
        dragMomentum={false}
        onDragStart={() => {
          setIsDragging(true);
          if (!hasInteractedOnce) {
            setHasInteractedOnce(true);
            try {
              localStorage.setItem('garden_can_drag_guided', 'true');
            } catch {}
          }
        }}
        onDrag={handleDrag}
        onDragEnd={handleDragEnd}
        animate={{
          x: isDragging ? undefined : pos.x,
          y: isDragging ? undefined : pos.y,
          scale: isDragging ? 1.12 : 1,
        }}
        transition={{
          x: { type: 'spring', stiffness: 220, damping: 26 },
          y: { type: 'spring', stiffness: 220, damping: 26 },
          scale: { duration: 0.2 },
        }}
        onClick={() => {
          if (!isDragging) {
            const nextIdx = activeFlowerTarget !== null ? (activeFlowerTarget + 1) % 4 : 0;
            triggerWaterFlower(nextIdx);
          }
        }}
        whileHover={{ scale: isDragging ? 1.12 : 1.08 }}
        whileTap={{ scale: 0.94 }}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          transform: 'translate(-50%, -50%)',
          willChange: 'transform',
        }}
        className={`pointer-events-auto flex flex-col items-center select-none touch-none ${
          isEmpty ? 'cursor-not-allowed opacity-75' : 'cursor-grab active:cursor-grabbing'
        }`}
        id="physical-watering-can"
      >
        {/* Subtle breathing glow when resting with water on the meadow */}
        {!isDragging && !isTilting && !isEmpty && (
          <motion.div
            animate={{
              scale: [0.95, 1.15, 0.95],
              opacity: [0.35, 0.75, 0.35],
            }}
            transition={{
              duration: 2.8,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
            className="absolute -inset-3 rounded-full bg-sky-400/25 blur-md pointer-events-none -z-10"
          />
        )}

        {/* 3D BOTANICAL METALLIC WATERING CAN GRAPHIC (Only the can rotates!) */}
        <div className="relative w-[76px] h-[76px] sm:w-20 sm:h-20">
          <motion.div
            animate={{
              rotate: isTilting ? -38 : isDragging ? dragAngle : 0,
            }}
            transition={{
              rotate: { duration: 0.35, ease: [0.34, 1.56, 0.64, 1] },
            }}
            style={{
              transformOrigin: '55% 55%',
              willChange: 'transform',
              backfaceVisibility: 'hidden',
            }}
            className="w-full h-full"
          >
            <svg
              viewBox="0 0 64 64"
              className="w-full h-full filter drop-shadow-[0_8px_16px_rgba(0,0,0,0.35)] overflow-visible"
            >
              {/* Subtle Ground Shadow when resting */}
              <ellipse cx="33" cy="56" rx="20" ry="4.5" fill="rgba(0,0,0,0.22)" />

              {/* Back Handle */}
              <path
                d="M46 25 C54 25, 55 42, 44 44"
                fill="none"
                stroke="#0369a1"
                strokeWidth="4.2"
                strokeLinecap="round"
              />
              <path
                d="M46 25 C54 25, 55 42, 44 44"
                fill="none"
                stroke="#38bdf8"
                strokeWidth="2.2"
                strokeLinecap="round"
                opacity="0.8"
              />

              {/* Main Tank Barrel */}
              <rect
                x="18"
                y="18"
                width="28"
                height="35"
                rx="6"
                fill="url(#canBodyGradient)"
                stroke="#0284c7"
                strokeWidth="1.2"
              />

              {/* Liquid Level Indicator Inside Tank Window */}
              <g clipPath="url(#tankInteriorClip)">
                <rect
                  x="19"
                  y={53 - (33 * Math.max(0, canWaterLevel)) / 100}
                  width="26"
                  height={(33 * Math.max(0, canWaterLevel)) / 100}
                  fill="url(#waterFillGrad)"
                  opacity={isEmpty ? 0.2 : 0.85}
                />
                {/* Water surface meniscus */}
                {!isEmpty && (
                  <ellipse
                    cx="32"
                    cy={53 - (33 * Math.max(0, canWaterLevel)) / 100}
                    rx="12"
                    ry="2"
                    fill="#e0f2fe"
                    opacity="0.9"
                  />
                )}
              </g>

              {/* Metallic Highlights & Specular Rim */}
              <rect
                x="20"
                y="20"
                width="2.5"
                height="31"
                rx="1.2"
                fill="#ffffff"
                fillOpacity="0.38"
              />

              {/* Top Fill Opening Rim */}
              <ellipse
                cx="32"
                cy="18"
                rx="12"
                ry="3.5"
                fill="#0284c7"
                stroke="#38bdf8"
                strokeWidth="1"
              />
              <ellipse cx="32" cy="18" rx="9" ry="2.2" fill="#0369a1" />

              {/* Over-the-top Curved Carrying Handle */}
              <path
                d="M24 18 C24 8, 40 8, 40 18"
                fill="none"
                stroke="#0369a1"
                strokeWidth="3.6"
                strokeLinecap="round"
              />
              <path
                d="M24 18 C24 8, 40 8, 40 18"
                fill="none"
                stroke="#7dd3fc"
                strokeWidth="1.6"
                strokeLinecap="round"
                opacity="0.9"
              />

              {/* Elongated Spout Neck */}
              <path
                d="M18 38 L6 28 V23 L18 32 Z"
                fill="url(#spoutBodyGrad)"
                stroke="#0284c7"
                strokeWidth="1"
              />
              <path
                d="M18 33 L7 24.5"
                fill="none"
                stroke="#bae6fd"
                strokeWidth="1.2"
                strokeLinecap="round"
                opacity="0.75"
              />

              {/* Sprinkler Rose Head with Perforated Nozzles (origin of water flow) */}
              <ellipse
                cx="5"
                cy="25"
                rx="3.2"
                ry="5.8"
                transform="rotate(28 5 25)"
                fill="#38bdf8"
                stroke="#0284c7"
                strokeWidth="1.2"
              />
              {/* Spray Holes */}
              <circle cx="4.5" cy="23.5" r="0.65" fill="#0369a1" />
              <circle cx="5.2" cy="25" r="0.65" fill="#0369a1" />
              <circle cx="4.8" cy="26.5" r="0.65" fill="#0369a1" />

              {/* Heart Emblem */}
              <path
                d="M32 37 C32 37, 28 33, 28 30 C28 27.5, 30 26, 32 28 C34 26, 36 27.5, 36 30 C36 33, 32 37, 32 37 Z"
                fill="#ffffff"
                fillOpacity={isEmpty ? 0.25 : 0.7}
              />

              <clipPath id="tankInteriorClip">
                <rect x="19" y="19" width="26" height="33" rx="4" />
              </clipPath>

              <defs>
                <linearGradient id="canBodyGradient" x1="18" y1="18" x2="46" y2="53" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#38bdf8" />
                  <stop offset="0.45" stopColor="#0284c7" />
                  <stop offset="0.9" stopColor="#0369a1" />
                  <stop offset="1" stopColor="#0c4a6e" />
                </linearGradient>
                <linearGradient id="spoutBodyGrad" x1="18" y1="38" x2="6" y2="23" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#0284c7" />
                  <stop offset="1" stopColor="#38bdf8" />
                </linearGradient>
                <linearGradient id="waterFillGrad" x1="18" y1="20" x2="18" y2="53" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#67e8f9" />
                  <stop offset="0.5" stopColor="#06b6d4" />
                  <stop offset="1" stopColor="#0891b2" />
                </linearGradient>
              </defs>
            </svg>
          </motion.div>
        </div>

        {/* DYNAMIC ACTION LABEL: Stays upright, compact, never overflows */}
        <div className="mt-1 flex items-center gap-1 text-[10px] text-white/95 bg-slate-950/80 backdrop-blur-md px-2.5 py-0.5 rounded-full border border-white/20 shadow-lg pointer-events-none max-w-[140px] truncate text-center">
          <Sparkles className="w-2.5 h-2.5 text-amber-300 shrink-0" />
          <span className="truncate">
            {isEmpty
              ? 'Vacía (0%)'
              : isTilting
              ? 'Regando'
              : isDragging
              ? 'Suelta en flor'
              : `${canWaterLevel}% Agua`}
          </span>
        </div>
      </motion.div>
    </div>
  );
});

ManualGardenCan.displayName = 'ManualGardenCan';
